import { bannerText, scheduleKind, scheduleLabel, tileDay, tileLabel, timeLines, type Schedule, type ScheduleKind } from '../lib/schedule';
import { useLocalDay } from '../hooks/useLocalDay';

// When a job is booked for, drawn the way Bobby approved on 1 Nov 2026 (PR-43, v7):
// a fixed tile on every Live Jobs row and a one-line banner on the job page, in the same
// amber as the phone for every kind.

// One amber for every kind (tag system spec, 1 Nov 2026): the words say which.
const AMBER = { background: 'var(--booking-fill)', color: 'var(--booking-ink)' };
const COLOURS: Record<ScheduleKind, { background: string; color: string }> = {
  preBooked: AMBER,
  today: AMBER,
  asap: AMBER,
  past: AMBER,
};

/** 62 × 56 px, every row, the same size whatever it holds; the block centred both ways. */
export function ScheduleTile({ schedule }: { schedule: Schedule }) {
  const now = new Date(useLocalDay());
  const kind = scheduleKind(schedule, now);
  const lines = timeLines(schedule);
  return (
    <span
      role="img"
      aria-label={scheduleLabel(schedule, now)}
      title={scheduleLabel(schedule, now)}
      className="schedule-tile flex min-h-[3.5rem] w-[3.875rem] shrink-0 flex-col items-center justify-center rounded-[8px] px-px py-1 text-center leading-[1.1]"
      style={COLOURS[kind]}
    >
      {kind === 'asap' ? (
        <span className="font-mono text-[15px] font-bold tracking-[0.04em]">ASAP</span>
      ) : (
        <>
          <span className="font-mono text-[9px] font-semibold">{tileLabel(schedule, now)}</span>
          <span className="text-[19px] font-bold tracking-[-0.02em]">{tileDay(schedule)}</span>
          {lines && (
            <span className="font-mono text-[10.5px] font-semibold tracking-[-0.03em]">
              {lines.join('').length <= 9 ? lines.join('') : lines.map((line) => <span key={line} className="block">{line}</span>)}
            </span>
          )}
        </>
      )}
    </span>
  );
}

/** "PRE-BOOKED  Wed 31 Dec · 10:30am–1:30pm" (amber) or "Today · 11:30am" (blue); nothing when no date. */
export function ScheduleBanner({ schedule }: { schedule: Schedule }) {
  const now = new Date(useLocalDay());
  const kind = scheduleKind(schedule, now);
  const text = bannerText(schedule, now);
  if (kind === 'asap' || !text) return null;
  return (
    <div
      className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1 rounded-[8px] px-3.5 py-2.5"
      style={COLOURS[kind]}
      aria-label={scheduleLabel(schedule, now)}
    >
      {kind === 'preBooked' && (
        <span className="font-mono text-[11px] font-semibold tracking-[0.08em]">PRE-BOOKED</span>
      )}
      <span className="break-words text-[15px] font-semibold">{text}</span>
    </div>
  );
}
